import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import { useImportInputFormatSchemas } from '../../../services/llm-input-format.service';
import { InputFieldDef } from '../../../types/admin';

interface ImportSchemaDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (fields: InputFieldDef[]) => void;
}

export const ImportSchemaDialog: React.FC<ImportSchemaDialogProps> = ({
  open,
  onClose,
  onImport,
}) => {
  const importSchemas = useImportInputFormatSchemas();
  const [inputSchema, setInputSchema] = React.useState('');
  const [outputSchema, setOutputSchema] = React.useState('');
  const [warnings, setWarnings] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setInputSchema('');
    setOutputSchema('');
    setWarnings([]);
  }, [open]);

  const canConvert =
    (inputSchema.trim() !== '' || outputSchema.trim() !== '') && !importSchemas.isPending;

  const convert = async () => {
    setWarnings([]);
    const result = await importSchemas.mutateAsync({
      inputSchema: inputSchema.trim() || undefined,
      outputSchema: outputSchema.trim() || undefined,
    });
    onImport(result.fields);
    if (result.warnings.length === 0) {
      onClose();
      return;
    }
    setWarnings(result.warnings);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import from JSON Schema</DialogTitle>
      <DialogContent>
        <Box className="space-y-4 pt-2">
          <Alert severity="info">
            Fields are added to the format. Meaning (semantic) and “price depends on” are not
            guessed; set them by hand after importing. Existing fields with the same key keep those
            settings.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={16}
            label="Request (input) JSON Schema"
            value={inputSchema}
            onChange={(event) => setInputSchema(event.target.value)}
            inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={12}
            label="Response (output) JSON Schema"
            value={outputSchema}
            onChange={(event) => setOutputSchema(event.target.value)}
            inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          {warnings.length > 0 && (
            <Alert severity="warning">
              Fields were added, but please review:
              <Box component="ul" className="m-0 pl-4">
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </Box>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{warnings.length > 0 ? 'Done' : 'Cancel'}</Button>
        <Button
          variant="contained"
          disabled={!canConvert}
          onClick={convert}
          startIcon={importSchemas.isPending ? <CircularProgress size={16} /> : null}
        >
          Convert
        </Button>
      </DialogActions>
    </Dialog>
  );
};
